import type { ChatSessionDetailMessage } from '@/api/ai-chat';
import type { AiChatSourceRef } from '@/api/ai-chat/types';

import type { ChatApiStrategyKey } from '../service/types';
import { DEFAULT_CHAT_API_STRATEGY_KEY } from '../service/types';
import type { SessionMessage } from './message';
import { createMessageId } from './message';
import type { CustomerSessionPreview } from './sessionPath';

export interface ChatSession {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  lastMessage?: string;
  messages?: SessionMessage[];
  appId?: string;
  /** 空态所选策略；首条消息后锁定。 */
  apiStrategyKey?: ChatApiStrategyKey;
  /**
   * 详情接口 session.last_status。只决定打开详情时要不要续传，不从消息反推。
   * streaming 才续；completed、failed 不续。
   */
  lastStatus?: 'streaming' | 'completed' | 'failed';
  /** 服务端这一轮还在推。离开页面后用它续传。用户点停止就清掉。 */
  liveTurnId?: string;
  /** 切回正在输出的会话时挂起，掩盖重连期间的内容跳变，重连拿到首帧后揭掉。 */
  resumeLoading?: boolean;
  /**
   * 从管理端跳入时挂上的客服会话引用。只给本空白会话第一次发送用，发出后清空。
   * 续传、切会话、新任务都不读。叉掉引用卡片时一并清空。
   */
  pendingSourceRef?: AiChatSourceRef;
  /** 输入框上方引用卡片；与 pendingSourceRef 同生同灭。 */
  pendingSourcePreview?: CustomerSessionPreview;
  /** 预留模型配置、标签等会话维度扩展数据。 */
  metadata?: Record<string, unknown>;
}

export const createSession = (
  appId: string,
  apiStrategyKey: ChatApiStrategyKey = DEFAULT_CHAT_API_STRATEGY_KEY
): ChatSession => {
  const timestamp = new Date().toISOString();
  return {
    id: createMessageId('session'),
    appId,
    title: '新对话',
    createdAt: timestamp,
    updatedAt: timestamp,
    messages: [],
    apiStrategyKey,
  };
};

export const strategyKeyFromType = (
  sessionType: string
): ChatApiStrategyKey | undefined => {
  if (sessionType === 'smart_cs_tuning') return 'optimization';
  if (sessionType === 'aisee_agent') return 'agent';
  return undefined;
};

export const detailPlaceholder = (sessionId: string): SessionMessage => ({
  id: `loading-${sessionId}`,
  type: 'loading',
  content: '',
  roundNumber: 1,
  createdAt: new Date().toISOString(),
  status: 'pending',
});

export const toSessionMessages = (
  messages: ChatSessionDetailMessage[]
): SessionMessage[] =>
  messages.map((message) => ({
    id: message.id,
    type: message.type,
    content: message.content,
    roundNumber: message.roundNumber,
    createdAt: message.createdAt,
    status: message.status,
    ...(message.progress ? { progress: message.progress } : {}),
    ...(message.trace?.length ? { trace: message.trace } : {}),
    ...(message.clarifications?.length
      ? { clarifications: message.clarifications }
      : {}),
    ...(message.proposal ? { proposal: message.proposal } : {}),
    ...(message.replay?.length ? { replay: message.replay } : {}),
    ...(message.blocks?.length ? { blocks: message.blocks } : {}),
    ...(message.attachments?.length
      ? { attachments: message.attachments }
      : {}),
    ...(message.sourcePreview ? { sourcePreview: message.sourcePreview } : {}),
    ...(message.artifacts?.length ? { artifacts: message.artifacts } : {}),
    ...(typeof message.timeUsed === 'number'
      ? { timeUsed: message.timeUsed }
      : {}),
  }));
